import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAllHealthData, getStoredTokens } from "@/services/huaweiHealth";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import {
  Footprints,
  Heart,
  Moon,
  Activity,
  Flame,
  Gauge,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Watch,
  AlertCircle,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";

export const WearableDataWidget = ({ compact = false }) => {
  const [healthData, setHealthData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("today");

  useEffect(() => {
    loadHealthData();
  }, []);

  const loadHealthData = async () => {
    setIsLoading(true);
    try {
      const tokens = getStoredTokens();
      const data = await getAllHealthData(tokens?.access_token);
      setHealthData(data);
    } catch (error) {
      console.error('Error loading health data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">Caricamento dati...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!healthData) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-center">
            <Watch className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Nessun dispositivo connesso</p>
            <Button variant="link">Connetti un dispositivo</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { today, trends, heartRateTimeline, isDemo } = healthData;

  // Compact version for home page
  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Watch className="h-5 w-5 text-primary" />
              Dati Wearable
            </CardTitle>
            {isDemo && (
              <Badge variant="secondary" className="text-xs">Demo</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Steps */}
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-2 mb-1">
                <Footprints className="h-4 w-4 text-primary" />
                <span className="text-xs text-muted-foreground">Passi</span>
              </div>
              <p className="text-xl font-bold">{today.steps.toLocaleString()}</p>
            </div>

            {/* Heart Rate */}
            <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20">
              <div className="flex items-center gap-2 mb-1">
                <Heart className="h-4 w-4 text-destructive" />
                <span className="text-xs text-muted-foreground">BPM</span>
              </div>
              <p className="text-xl font-bold">{today.heartRate.current}</p>
            </div>

            {/* SpO2 */}
            <div className="p-3 rounded-lg bg-info/5 border border-info/20">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="h-4 w-4 text-info" />
                <span className="text-xs text-muted-foreground">SpO2</span>
              </div>
              <p className="text-xl font-bold">{today.spo2}%</p>
            </div>

            {/* Sleep */}
            <div className="p-3 rounded-lg bg-secondary/5 border border-secondary/20">
              <div className="flex items-center gap-2 mb-1">
                <Moon className="h-4 w-4 text-secondary" />
                <span className="text-xs text-muted-foreground">Sonno</span>
              </div>
              <p className="text-xl font-bold">{today.sleep.duration.toFixed(1)}h</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full version
  return (
    <div className="space-y-6">
      {isDemo && (
        <Card className="bg-warning/10 border-warning/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-warning" />
              <div>
                <p className="font-medium text-warning">Dati Demo</p>
                <p className="text-sm text-muted-foreground">
                  Questi sono dati simulati. Connetti il tuo Huawei per dati reali.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="today">Oggi</TabsTrigger>
          <TabsTrigger value="trends">Trend Settimanale</TabsTrigger>
        </TabsList>

        {/* Today's Data */}
        <TabsContent value="today" className="mt-6 space-y-6">
          {/* Main Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Steps */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Passi</p>
                    <p className="text-3xl font-bold">{today.steps.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{today.distance} km</p>
                  </div>
                  <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Footprints className="h-7 w-7 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Heart Rate */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Frequenza Cardiaca</p>
                    <p className="text-3xl font-bold">{today.heartRate.current} <span className="text-lg font-normal">bpm</span></p>
                    <p className="text-xs text-muted-foreground">
                      Min {today.heartRate.min} / Max {today.heartRate.max}
                    </p>
                  </div>
                  <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                    <Heart className="h-7 w-7 text-destructive animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SpO2 */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Saturazione O2</p>
                    <p className="text-3xl font-bold">{today.spo2}<span className="text-lg font-normal">%</span></p>
                    <Badge className={cn(
                      "mt-1",
                      today.spo2 >= 95 ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
                    )}>
                      {today.spo2 >= 95 ? "Normale" : "Attenzione"}
                    </Badge>
                  </div>
                  <div className="h-14 w-14 rounded-full bg-info/10 flex items-center justify-center">
                    <Activity className="h-7 w-7 text-info" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Calories */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Calorie</p>
                    <p className="text-3xl font-bold">{today.calories.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">kcal totali</p>
                  </div>
                  <div className="h-14 w-14 rounded-full bg-warning/10 flex items-center justify-center">
                    <Flame className="h-7 w-7 text-warning" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stress */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Livello Stress</p>
                    <p className="text-3xl font-bold">{today.stress}</p>
                    <Badge className={cn(
                      "mt-1",
                      today.stress < 40 ? "bg-success/10 text-success" : 
                      today.stress < 60 ? "bg-warning/10 text-warning" : "bg-destructive/10 text-destructive"
                    )}>
                      {today.stress < 40 ? "Basso" : today.stress < 60 ? "Medio" : "Alto"}
                    </Badge>
                  </div>
                  <div className="h-14 w-14 rounded-full bg-secondary/10 flex items-center justify-center">
                    <Gauge className="h-7 w-7 text-secondary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sleep */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Sonno</p>
                    <p className="text-3xl font-bold">{today.sleep.duration.toFixed(1)}<span className="text-lg font-normal">h</span></p>
                    <p className="text-xs text-muted-foreground">
                      Qualità: {today.sleep.quality}%
                    </p>
                  </div>
                  <div className="h-14 w-14 rounded-full bg-indigo-500/10 flex items-center justify-center">
                    <Moon className="h-7 w-7 text-indigo-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Heart Rate Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Frequenza Cardiaca Oggi</CardTitle>
              <CardDescription>Andamento durante la giornata</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={heartRateTimeline}>
                    <defs>
                      <linearGradient id="heartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[40, 120]} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} 
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="hsl(var(--destructive))"
                      strokeWidth={2}
                      fill="url(#heartGradient)"
                      name="BPM"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Sleep Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Analisi Sonno</CardTitle>
              <CardDescription>Fasi del sonno di stanotte</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-indigo-700" />
                    <span className="text-sm">Sonno Profondo</span>
                  </div>
                  <span className="font-medium">{today.sleep.deep.toFixed(1)}h</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-indigo-400" />
                    <span className="text-sm">Sonno Leggero</span>
                  </div>
                  <span className="font-medium">{today.sleep.light.toFixed(1)}h</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <span className="text-sm">REM</span>
                  </div>
                  <span className="font-medium">{today.sleep.rem.toFixed(1)}h</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-300" />
                    <span className="text-sm">Sveglio</span>
                  </div>
                  <span className="font-medium">{today.sleep.awake.toFixed(1)}h</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Trends */}
        <TabsContent value="trends" className="mt-6 space-y-6">
          {/* Steps Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Footprints className="h-5 w-5 text-primary" />
                Passi - Ultima Settimana
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends.steps}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Passi" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Heart Rate Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Heart className="h-5 w-5 text-destructive" />
                Frequenza Cardiaca a Riposo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends.heartRate}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[50, 90]} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} 
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="hsl(var(--destructive))"
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--destructive))' }}
                      name="BPM"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Sleep Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Moon className="h-5 w-5 text-indigo-500" />
                Durata Sonno
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends.sleep}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[0, 10]} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="value" fill="hsl(250, 60%, 50%)" radius={[4, 4, 0, 0]} name="Ore" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WearableDataWidget;
